import { Award, Users, Zap, Heart } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Hero Section */}
      <div className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" data-testid="about-page-title">
            About Avex Enterprises
          </h1>
          <p className="text-lg text-[#4A4A4A] max-w-3xl">
            Premium lighting solutions provider serving Nepal with excellence since our inception.
            We bring light to spaces through carefully curated products and exceptional service.
          </p>
        </div>
      </div>

      {/* Our Story */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-8 lg:p-12 border border-[#E5E5E5]">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-6">Our Story</h2>
            <div className="space-y-4 text-[#4A4A4A] leading-relaxed">
              <p>
                Avex Enterprises was founded with a vision to transform spaces across Nepal through
                premium lighting solutions. Based in Biratnagar, we've grown to become a trusted name
                in the lighting industry, serving residential, commercial, and architectural clients
                throughout the country.
              </p>
              <p>
                Our journey began with a simple belief: that the right lighting can completely transform
                a space, enhancing not just its aesthetics but also the mood and functionality. This
                philosophy continues to guide everything we do.
              </p>
              <p>
                Today, we offer a comprehensive range of lighting solutions—from elegant chandeliers
                for luxury homes to efficient commercial lighting for businesses. Every product in our
                collection is carefully selected to meet our high standards of quality, design, and value.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#BFA15F] rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-3">Quality First</h3>
              <p className="text-[#4A4A4A] text-sm">
                We source only the finest lighting products that meet international quality standards
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#BFA15F] rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-3">Customer Focus</h3>
              <p className="text-[#4A4A4A] text-sm">
                Your satisfaction is our priority, with personalized service and expert guidance
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#BFA15F] rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-3">Innovation</h3>
              <p className="text-[#4A4A4A] text-sm">
                Staying ahead with the latest lighting technologies and design trends
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#BFA15F] rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-3">Integrity</h3>
              <p className="text-[#4A4A4A] text-sm">
                Honest communication, transparent pricing, and reliable service in every interaction
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-center mb-12">What We Offer</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 border border-[#E5E5E5]">
              <h3 className="font-heading text-xl font-semibold mb-4">Residential Lighting</h3>
              <p className="text-[#4A4A4A] mb-4">
                Transform your home with our extensive collection of chandeliers, decorative lamps,
                wall lights, and ceiling fixtures.
              </p>
              <ul className="space-y-2 text-sm text-[#4A4A4A]">
                <li>• Living room lighting</li>
                <li>• Bedroom fixtures</li>
                <li>• Kitchen lighting</li>
                <li>• Outdoor illumination</li>
              </ul>
            </div>

            <div className="bg-white p-8 border border-[#E5E5E5]">
              <h3 className="font-heading text-xl font-semibold mb-4">Commercial Solutions</h3>
              <p className="text-[#4A4A4A] mb-4">
                Professional lighting systems designed for offices, retail spaces, hotels, and
                restaurants throughout Nepal.
              </p>
              <ul className="space-y-2 text-sm text-[#4A4A4A]">
                <li>• Office lighting</li>
                <li>• Retail spotlights</li>
                <li>• Hotel chandeliers</li>
                <li>• Restaurant ambiance</li>
              </ul>
            </div>

            <div className="bg-white p-8 border border-[#E5E5E5]">
              <h3 className="font-heading text-xl font-semibold mb-4">Consultation & Support</h3>
              <p className="text-[#4A4A4A] mb-4">
                Expert guidance from our team to help you choose the perfect lighting for your
                specific needs and budget.
              </p>
              <ul className="space-y-2 text-sm text-[#4A4A4A]">
                <li>• Free consultation</li>
                <li>• Project planning</li>
                <li>• Installation guidance</li>
                <li>• After-sales support</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#1A1A1A] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
            Ready to Illuminate Your Space?
          </h2>
          <p className="text-white/80 mb-8">
            Contact us today to discuss your lighting needs and discover how we can help transform your environment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-[#BFA15F] text-white hover:bg-[#A68B4F] rounded-none px-8 py-3 uppercase tracking-widest text-sm font-medium transition-all duration-300"
            >
              Contact Us
            </a>
            <a
              href="/products"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1A1A1A] rounded-none px-8 py-3 uppercase tracking-widest text-sm font-medium transition-all duration-300"
            >
              View Products
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;